import React from 'react'

import { IubendaPreference, IubendaPurpose } from '../../types/iubenda'
const iubendaPurposeMap: Record<IubendaPurpose, 1 | 2 | 3 | 4 | 5> = {
  necessary: 1,
  functionality: 2,
  experience: 3,
  measurement: 4,
  marketing: 5,
}

let currentPreference: IubendaPreference = {
  purposes: { 1: true, 2: false, 3: false, 4: false, 5: false },
}

const listeners = new Map<string, (preference: IubendaPreference) => void>()
export const addIubendaListener = (callback: (preference: IubendaPreference) => void) => {
  const id = Math.random().toString(36).substring(2, 15)
  listeners.set(id, callback)
  return () => {
    listeners.delete(id)
  }
}

export const useIubendaPreference = () => {
  const [preference, setPreference] = React.useState<IubendaPreference>(currentPreference)
  React.useEffect(() => addIubendaListener(setPreference), [])
  return preference
}

/**
 * Function to check if the user has given consent for a specific purpose
 * @param purpose IubendaPurpose
 * @description Check if the user has given consent for a specific purpose
 * @example useIubendaConsent(2) // returns true if the user has given consent for functionality
 * @returns boolean which indicates if the user has given consent for a specific purpose
 */
export const useIubendaConsent = (purpose: IubendaPurpose): boolean => {
  const preference = useIubendaPreference()
  if (preference && 'purposes' in preference) {
    return preference.purposes[iubendaPurposeMap[purpose]]
  } else {
    return true
  }
}

export const openIubendaPreferences = () => {
  if (window._iub && window._iub.cs && window._iub.cs.ui) {
    window._iub.cs.ui.openPreferences()
  }
}

export const mountThirdPartyScript = (purpose: IubendaPurpose, factory: (script: HTMLScriptElement) => void) => {
  const script = document.createElement('script')
  script.type = 'text/plain'
  script.defer = true
  script.setAttribute('class', '_iub_cs_activate-inline')
  script.setAttribute('data-iub-purposes', `${iubendaPurposeMap[purpose]}`)
  factory(script)
  document.body.appendChild(script)
}

// Iubenda cookies GDPR stuff
// https://www.iubenda.com/en/help/1205-how-to-configure-your-cookie-solution-advanced-guide#api
// IMPORTANT NOTE: if you are copy pasting this snippet from the iubenda web take a look to not override the callbacks
export const loadIubenda = () => {
  // no cookie popup on privacy page (https://airfocus.com/privacy/)
  // visitor has to be able to see the privacy policy without accepting cookies
  if (window.location.pathname.includes('privacy')) {
    return
  }

  if (!window._iub) {
    window._iub = {
      csConfiguration: {
        gdprAppliesGlobally: false,
        lgpdAppliesGlobally: false,
        askConsentAtCookiePolicyUpdate: true,
        countryDetection: true,
        cookiePolicyInOtherWindow: true,
        enableFadp: true,
        enableLgpd: true,
        enableUspr: true,
        perPurposeConsent: true,
        purposes: '1, 2, 4, 5',
        lang: 'en',
        emitGtmEvents: true,
        siteId: 1167550,
        whitelabel: false,
        cookiePolicyId: 91277624,
        cookiePolicyUrl: 'https://airfocus.com/privacy/',
        i18n: {
          en: {
            banner: {
              dynamic: {
                body: "<div id='iubenda-cs-title'>Here, have a cookie &#x1f36a;</div><div id='iubenda-cs-paragraph'>We and selected partners use cookies or similar technologies as specified in the <a href='https://airfocus.com/privacy/' class='iubenda-cs-cookie-policy-lnk' target='_blank' rel='noopener'>cookie policy</a> to track the performance of the website, and to provide a better user experience. We don't store any personal details. You can consent to the use of such technologies by closing this notice.</div>",
              },
            },
          },
        },
        banner: {
          customizeButtonDisplay: true,
          acceptButtonColor: '#33b6ff',
          acceptButtonDisplay: true,
          backgroundOverlay: true,
          closeButtonDisplay: false,
          explicitWithdrawal: true,
          position: 'float-center',
          showTitle: false,
        },
        callback: {
          onPreferenceExpressedOrNotNeeded(preference: IubendaPreference | undefined) {
            currentPreference = preference
            listeners.forEach((callback) => callback(preference))
          },
        },
      },
    }
    document.head.appendChild(
      Object.assign(document.createElement('script'), {
        type: 'text/javascript',
        src: '//cs.iubenda.com/sync/1167550.js',
      })
    )
    document.head.appendChild(
      Object.assign(document.createElement('script'), {
        type: 'text/javascript',
        src: '//cdn.iubenda.com/cs/gpp/stub.js',
      })
    )
    document.head.appendChild(
      Object.assign(document.createElement('script'), {
        type: 'text/javascript',
        src: '//cdn.iubenda.com/cs/iubenda_cs.js',
        async: true,
      })
    )
  }
}
